/**
 * BenchmarkVisualization - Three.js neural network loading screen
 *
 * Renders a particle-connected network where each node = a scenario.
 * Nodes pulse while pending, light up with PoP colors when complete.
 * Layered emissive meshes create glow without postprocessing.
 */

import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Billboard, Text } from '@react-three/drei';
import * as THREE from 'three';
import { Box, Typography } from '@mui/material';
import { vortxTypography, TerminalConsole, ConsoleLine } from '@vortx/shared-ui';

const PHD_PURPLE = '#9b59b6';
const PHD_PURPLE_BRIGHT = '#c084e0';
const SUCCESS_COLOR = '#5fd4c4';
const MID_COLOR = '#9b59b6';
const FAIL_COLOR = '#ff4466';
const PENDING_COLOR = '#444455';
const EDGE_COLOR = '#9b59b640';

const tempObject = new THREE.Object3D();
const tempVec = new THREE.Vector3();

// Generate node positions in a 3D cluster around center
function generateNodePositions(count) {
    if (count <= 0) return [];

    const positions = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    if (count <= 4) {
        // Small: spread around center with vertical variation
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            const r = 2.5;
            positions.push([
                Math.cos(angle) * r,
                Math.sin(i * goldenAngle) * 0.8,
                Math.sin(angle) * r,
            ]);
        }
    } else if (count <= 10) {
        // Medium: two rings with z-depth
        const innerCount = Math.ceil(count * 0.4);
        const outerCount = count - innerCount;
        for (let i = 0; i < innerCount; i++) {
            const angle = (i / innerCount) * Math.PI * 2 + 0.3;
            const r = 1.5;
            positions.push([
                Math.cos(angle) * r,
                Math.sin(i * goldenAngle) * 0.8,
                Math.sin(angle) * r,
            ]);
        }
        for (let i = 0; i < outerCount; i++) {
            const angle = (i / outerCount) * Math.PI * 2;
            const r = 3.2;
            positions.push([
                Math.cos(angle) * r,
                Math.sin(i * goldenAngle * 2) * 0.6,
                Math.sin(angle) * r,
            ]);
        }
    } else {
        // Large: spherical Fibonacci distribution
        for (let i = 0; i < count; i++) {
            const theta = goldenAngle * i;
            const y = 1 - (2 * i) / (count - 1);
            const radius = Math.sqrt(1 - y * y) * 3.0;
            positions.push([
                Math.cos(theta) * radius,
                y * 1.8,
                Math.sin(theta) * radius,
            ]);
        }
    }
    return positions;
}

// Generate edges between nearby nodes + hub spokes to center
function generateEdges(positions) {
    const edges = [];
    const n = positions.length;
    if (n < 2) return edges;

    for (let i = 0; i < n; i++) {
        const distances = [];
        for (let j = 0; j < n; j++) {
            if (i === j) continue;
            const dx = positions[i][0] - positions[j][0];
            const dy = positions[i][1] - positions[j][1];
            const dz = positions[i][2] - positions[j][2];
            distances.push({ j, d: Math.sqrt(dx * dx + dy * dy + dz * dz) });
        }
        distances.sort((a, b) => a.d - b.d);
        const connectCount = Math.min(2, distances.length);
        for (let k = 0; k < connectCount; k++) {
            const j = distances[k].j;
            const key = Math.min(i, j) + '-' + Math.max(i, j);
            if (!edges.find(e => e.key === key)) {
                edges.push({ from: i, to: j, key });
            }
        }
    }
    return edges;
}

function getNodeColor(state, popPct) {
    if (state === 'completed') {
        if (popPct >= 85) return SUCCESS_COLOR;
        if (popPct >= 70) return MID_COLOR;
        return FAIL_COLOR;
    }
    if (state === 'running') return PHD_PURPLE_BRIGHT;
    if (state === 'failed') return FAIL_COLOR;
    return PENDING_COLOR;
}

// --- Scenario Node with multi-layer glow ---
function ScenarioNode({ position, state, popPct, index, label }) {
    const groupRef = useRef();
    const coreRef = useRef();
    const glowInnerRef = useRef();
    const glowOuterRef = useRef();
    const ringRef = useRef();
    const timeOffset = useMemo(() => index * 0.7 + Math.random() * 2, [index]);

    const nodeColor = useMemo(() => getNodeColor(state, popPct), [state, popPct]);
    const isActive = state === 'running' || state === 'completed';
    const isRunning = state === 'running';
    const isComplete = state === 'completed';

    useFrame(({ clock }) => {
        if (!coreRef.current) return;
        const t = clock.elapsedTime + timeOffset;

        if (state === 'pending') {
            const s = 0.85 + Math.sin(t * 1.2) * 0.1;
            coreRef.current.scale.setScalar(s);
            coreRef.current.material.opacity = 0.2 + Math.sin(t * 0.8) * 0.08;
            coreRef.current.rotation.y += 0.003;
            if (glowInnerRef.current) glowInnerRef.current.material.opacity = 0.04;
            if (glowOuterRef.current) glowOuterRef.current.material.opacity = 0.02;
        } else if (isRunning) {
            const s = 1.0 + Math.sin(t * 3.5) * 0.2;
            coreRef.current.scale.setScalar(s);
            coreRef.current.rotation.y += 0.03;
            coreRef.current.rotation.x += 0.01;
            coreRef.current.material.opacity = 0.8;
            coreRef.current.material.emissiveIntensity = 1.0 + Math.sin(t * 4) * 0.5;
            if (glowInnerRef.current) {
                glowInnerRef.current.material.opacity = 0.2 + Math.sin(t * 3) * 0.1;
                glowInnerRef.current.scale.setScalar(2.0 + Math.sin(t * 2.5) * 0.3);
            }
            if (glowOuterRef.current) {
                glowOuterRef.current.material.opacity = 0.08 + Math.sin(t * 2) * 0.04;
                glowOuterRef.current.scale.setScalar(3.0 + Math.sin(t * 1.8) * 0.5);
            }
        } else if (isComplete || state === 'failed') {
            coreRef.current.scale.setScalar(1.15);
            coreRef.current.material.opacity = 0.95;
            coreRef.current.material.emissiveIntensity = 0.6;
            coreRef.current.rotation.y += 0.002;
            groupRef.current.position.y = position[1] + Math.sin(t * 0.6) * 0.04;
            if (glowInnerRef.current) {
                glowInnerRef.current.material.opacity = 0.15;
                glowInnerRef.current.scale.setScalar(1.8);
            }
            if (glowOuterRef.current) {
                glowOuterRef.current.material.opacity = 0.06;
                glowOuterRef.current.scale.setScalar(2.5);
            }
        }

        // Orbital ring
        if (ringRef.current) {
            ringRef.current.rotation.z += isRunning ? 0.04 : 0.005;
            ringRef.current.rotation.x = Math.sin(t * 0.4 + timeOffset) * 0.5;
            ringRef.current.material.opacity = isActive ? 0.25 : 0.06;
        }
    });

    return (
        <group ref={groupRef} position={position}>
            {/* Outer glow layer */}
            <mesh ref={glowOuterRef}>
                <sphereGeometry args={[0.35, 12, 12]} />
                <meshBasicMaterial
                    color={nodeColor}
                    transparent
                    opacity={0.02}
                    depthWrite={false}
                    side={THREE.BackSide}
                />
            </mesh>

            {/* Inner glow layer */}
            <mesh ref={glowInnerRef}>
                <sphereGeometry args={[0.35, 12, 12]} />
                <meshBasicMaterial
                    color={nodeColor}
                    transparent
                    opacity={0.05}
                    depthWrite={false}
                />
            </mesh>

            {/* Orbital ring */}
            <mesh ref={ringRef} rotation={[Math.PI / 3, 0, 0]}>
                <torusGeometry args={[0.42, 0.008, 8, 48]} />
                <meshBasicMaterial
                    color={nodeColor}
                    transparent
                    opacity={0.1}
                />
            </mesh>

            {/* Core node */}
            <mesh ref={coreRef}>
                <icosahedronGeometry args={[0.28, isComplete ? 2 : 1]} />
                <meshStandardMaterial
                    color={nodeColor}
                    wireframe={!isComplete && state !== 'failed'}
                    transparent
                    opacity={0.4}
                    emissive={nodeColor}
                    emissiveIntensity={isActive ? 0.8 : 0.15}
                />
            </mesh>

            {/* PoP label for completed nodes */}
            {isComplete && popPct != null && (
                <Billboard position={[0, 0.6, 0]}>
                    <Text
                        fontSize={0.24}
                        color={nodeColor}
                        anchorX="center"
                        anchorY="middle"
                        font={undefined}
                        outlineWidth={0.02}
                        outlineColor="#000000"
                    >
                        {`${popPct.toFixed(0)}%`}
                    </Text>
                </Billboard>
            )}

            {/* "RUNNING" indicator */}
            {isRunning && (
                <Billboard position={[0, 0.55, 0]}>
                    <Text
                        fontSize={0.12}
                        color={PHD_PURPLE_BRIGHT}
                        anchorX="center"
                        anchorY="middle"
                        font={undefined}
                    >
                        RUNNING
                    </Text>
                </Billboard>
            )}

            {/* Scenario label below node */}
            {label && (
                <Billboard position={[0, isComplete ? -0.65 : -0.55, 0]}>
                    <Text
                        fontSize={0.18}
                        color="#aaa"
                        anchorX="center"
                        anchorY="middle"
                        font={undefined}
                        outlineWidth={0.02}
                        outlineColor="#000000"
                        maxWidth={3}
                    >
                        {label}
                    </Text>
                </Billboard>
            )}
        </group>
    );
}

// --- Edge with particle flow ---
const PARTICLES_PER_EDGE = 8;

function NetworkEdge({ from, to, active }) {
    const instancedRef = useRef();
    const progressRef = useRef(
        Array.from({ length: PARTICLES_PER_EDGE }, () => Math.random())
    );

    const curve = useMemo(() => {
        const start = new THREE.Vector3(...from);
        const end = new THREE.Vector3(...to);
        const mid = new THREE.Vector3(
            (start.x + end.x) / 2,
            (start.y + end.y) / 2 + 0.4,
            (start.z + end.z) / 2,
        );
        return new THREE.QuadraticBezierCurve3(start, mid, end);
    }, [from, to]);

    const linePoints = useMemo(() => curve.getPoints(24), [curve]);
    const lineGeo = useMemo(() => new THREE.BufferGeometry().setFromPoints(linePoints), [linePoints]);
    const sphereGeo = useMemo(() => new THREE.SphereGeometry(0.035, 4, 4), []);

    useFrame((_, delta) => {
        if (!instancedRef.current || !active) return;

        const progs = progressRef.current;
        for (let i = 0; i < PARTICLES_PER_EDGE; i++) {
            progs[i] = (progs[i] + delta * (0.2 + i * 0.03)) % 1.0;
            curve.getPoint(progs[i], tempVec);
            tempObject.position.copy(tempVec);
            const s = 0.6 + Math.sin(progs[i] * Math.PI) * 0.6;
            tempObject.scale.setScalar(s);
            tempObject.updateMatrix();
            instancedRef.current.setMatrixAt(i, tempObject.matrix);
        }
        instancedRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <group>
            <line geometry={lineGeo}>
                <lineBasicMaterial
                    color={active ? PHD_PURPLE : EDGE_COLOR}
                    transparent
                    opacity={active ? 0.3 : 0.1}
                />
            </line>

            {active && (
                <instancedMesh
                    ref={instancedRef}
                    args={[sphereGeo, null, PARTICLES_PER_EDGE]}
                >
                    <meshBasicMaterial
                        color={PHD_PURPLE_BRIGHT}
                        transparent
                        opacity={0.6}
                    />
                </instancedMesh>
            )}
        </group>
    );
}

// --- Central core with orbital rings ---
function CentralCore({ completionPct }) {
    const coreRef = useRef();
    const ring1Ref = useRef();
    const ring2Ref = useRef();
    const glowRef = useRef();

    useFrame(({ clock }) => {
        if (!coreRef.current) return;
        const t = clock.elapsedTime;

        const baseScale = 0.5 + completionPct * 0.3;
        const pulse = Math.sin(t * 2.0) * 0.06;
        coreRef.current.scale.setScalar(baseScale + pulse);
        coreRef.current.rotation.y += 0.008;
        coreRef.current.rotation.x = Math.sin(t * 0.3) * 0.15;
        coreRef.current.material.emissiveIntensity = 0.4 + completionPct * 1.0 + Math.sin(t * 2.5) * 0.2;

        if (ring1Ref.current) {
            ring1Ref.current.rotation.z += 0.01;
            ring1Ref.current.rotation.x = Math.sin(t * 0.5) * 0.3;
            ring1Ref.current.material.opacity = 0.15 + completionPct * 0.15;
        }
        if (ring2Ref.current) {
            ring2Ref.current.rotation.z -= 0.007;
            ring2Ref.current.rotation.y = Math.cos(t * 0.4) * 0.4;
            ring2Ref.current.material.opacity = 0.1 + completionPct * 0.1;
        }
        if (glowRef.current) {
            const gs = 1.2 + completionPct * 0.8 + Math.sin(t * 1.5) * 0.15;
            glowRef.current.scale.setScalar(gs);
            glowRef.current.material.opacity = 0.06 + completionPct * 0.06;
        }
    });

    return (
        <group>
            {/* Core glow sphere */}
            <mesh ref={glowRef}>
                <sphereGeometry args={[0.6, 16, 16]} />
                <meshBasicMaterial
                    color={PHD_PURPLE}
                    transparent
                    opacity={0.05}
                    depthWrite={false}
                    side={THREE.BackSide}
                />
            </mesh>

            {/* Core wireframe */}
            <mesh ref={coreRef}>
                <icosahedronGeometry args={[0.5, 2]} />
                <meshStandardMaterial
                    color={PHD_PURPLE}
                    wireframe
                    transparent
                    opacity={0.7}
                    emissive={PHD_PURPLE}
                    emissiveIntensity={0.5}
                />
            </mesh>

            {/* Ring 1 */}
            <mesh ref={ring1Ref} rotation={[Math.PI / 4, 0, 0]}>
                <torusGeometry args={[0.9, 0.012, 8, 64]} />
                <meshBasicMaterial
                    color={PHD_PURPLE_BRIGHT}
                    transparent
                    opacity={0.15}
                />
            </mesh>

            {/* Ring 2 (perpendicular) */}
            <mesh ref={ring2Ref} rotation={[0, Math.PI / 3, Math.PI / 4]}>
                <torusGeometry args={[1.1, 0.008, 8, 64]} />
                <meshBasicMaterial
                    color={PHD_PURPLE_BRIGHT}
                    transparent
                    opacity={0.1}
                />
            </mesh>
        </group>
    );
}

// --- Ambient floating particles ---
const AMBIENT_COUNT = 60;

function AmbientParticles() {
    const instancedRef = useRef();
    const dataRef = useRef([]);

    const sphereGeo = useMemo(() => new THREE.SphereGeometry(0.018, 4, 4), []);

    useMemo(() => {
        dataRef.current = Array.from({ length: AMBIENT_COUNT }, () => ({
            pos: new THREE.Vector3(
                (Math.random() - 0.5) * 10,
                (Math.random() - 0.5) * 5,
                (Math.random() - 0.5) * 10,
            ),
            vel: new THREE.Vector3(
                (Math.random() - 0.5) * 0.15,
                (Math.random() - 0.5) * 0.08,
                (Math.random() - 0.5) * 0.15,
            ),
            phase: Math.random() * Math.PI * 2,
        }));
    }, []);

    useFrame(({ clock }, delta) => {
        if (!instancedRef.current) return;
        const t = clock.elapsedTime;

        dataRef.current.forEach((p, i) => {
            p.pos.addScaledVector(p.vel, delta);
            if (Math.abs(p.pos.x) > 5) p.vel.x *= -1;
            if (Math.abs(p.pos.y) > 2.5) p.vel.y *= -1;
            if (Math.abs(p.pos.z) > 5) p.vel.z *= -1;

            tempObject.position.copy(p.pos);
            const s = 0.5 + Math.sin(t * 1.2 + p.phase) * 0.5;
            tempObject.scale.setScalar(s);
            tempObject.updateMatrix();
            instancedRef.current.setMatrixAt(i, tempObject.matrix);
        });
        instancedRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <instancedMesh ref={instancedRef} args={[sphereGeo, null, AMBIENT_COUNT]}>
            <meshBasicMaterial color={PHD_PURPLE} transparent opacity={0.2} />
        </instancedMesh>
    );
}

// --- Terminal log using shared TerminalConsole ---
function BenchmarkTerminal({ scenarios, status, completedCount, totalScenarios }) {
    const [lines, setLines] = useState([]);
    const prevStatesRef = useRef({});
    const prevStatusRef = useRef(null);

    // Generate log lines from state transitions
    useEffect(() => {
        const newLines = [];
        const prevStates = prevStatesRef.current;

        // Run status change
        if (status !== prevStatusRef.current) {
            if (status === 'submitted' || status === 'queued') {
                newLines.push({ prefix: 'SYS', text: 'Benchmark submitted — waiting for runner...', color: PHD_PURPLE_BRIGHT });
            } else if (status === 'running' && prevStatusRef.current !== 'running') {
                newLines.push({ prefix: 'SYS', text: `Runner started — ${totalScenarios} scenarios queued`, color: PHD_PURPLE_BRIGHT });
            } else if (status === 'completed') {
                newLines.push({ prefix: 'SYS', text: `Benchmark complete — ${completedCount}/${totalScenarios} passed`, color: SUCCESS_COLOR });
            } else if (status === 'failed') {
                newLines.push({ prefix: 'SYS', text: 'Benchmark failed', color: FAIL_COLOR });
            }
            prevStatusRef.current = status;
        }

        // Per-scenario state transitions
        scenarios.forEach((s) => {
            const id = s.scenario_id;
            const prevState = prevStates[id];
            const cfg = s.config || {};
            const label = [cfg.building_type, cfg.climate_zone, cfg.tariff_variant].filter(Boolean).join(' / ') || id;

            // Build agent tag string from config
            const agents = [];
            if (cfg.ev_preset && cfg.ev_preset !== 'none') agents.push('EV');
            if (cfg.cold_preset && cfg.cold_preset !== 'none') agents.push('COLD');
            agents.push('BATT'); // always present
            const agentStr = agents.join('+');

            const emitRunning = () => {
                newLines.push({ prefix: 'RUN', text: `${label} [${agentStr}]`, color: '#aaa' });
            };

            const emitCompleted = () => {
                const pop = s.cost_pop_pct;
                const peakPop = s.peak_pop_pct;
                const popColor = pop >= 85 ? SUCCESS_COLOR : pop >= 70 ? PHD_PURPLE_BRIGHT : FAIL_COLOR;

                // Header line: scenario name + PoP
                newLines.push({
                    prefix: 'OK',
                    text: `${label} [${agentStr}] — PoP ${pop != null ? pop.toFixed(1) : '--'}%`,
                    color: popColor,
                });

                // Detail: peak comparison
                const siloedPeak = s.siloed_peak_kw;
                const orchPeak = s.orchestrated_peak_kw;
                const globalPeak = s.global_optimum_peak_kw;
                if (siloedPeak != null && orchPeak != null) {
                    const peakReduction = siloedPeak > 0 ? ((1 - orchPeak / siloedPeak) * 100).toFixed(0) : '--';
                    newLines.push({
                        prefix: '   ',
                        text: `Peak: ${siloedPeak.toFixed(0)} → ${orchPeak.toFixed(0)} kW (${peakReduction}% reduction, global: ${globalPeak?.toFixed(0) || '--'} kW)`,
                        color: '#777',
                    });
                }

                // Detail: cost comparison
                const siloedCost = s.siloed_cost;
                const orchCost = s.orchestrated_cost;
                const globalCost = s.global_optimum_cost;
                if (siloedCost != null && orchCost != null) {
                    const costSavings = siloedCost - orchCost;
                    newLines.push({
                        prefix: '   ',
                        text: `Cost: $${siloedCost.toFixed(0)} → $${orchCost.toFixed(0)} (saved $${costSavings.toFixed(0)}, global: $${globalCost?.toFixed(0) || '--'})`,
                        color: '#777',
                    });
                }

                // Detail: peak PoP if available and different from cost PoP
                if (peakPop != null && pop != null && Math.abs(peakPop - pop) > 1) {
                    newLines.push({
                        prefix: '   ',
                        text: `Peak PoP: ${peakPop.toFixed(1)}%`,
                        color: '#666',
                    });
                }
            };

            const emitFailed = () => {
                const errMsg = s.error || 'unknown error';
                newLines.push({ prefix: 'ERR', text: `${label} — ${errMsg}`, color: FAIL_COLOR });
            };

            if (!prevState && (s.status === 'running' || s.status === 'completed' || s.status === 'failed')) {
                if (s.status === 'running') emitRunning();
                else if (s.status === 'completed') emitCompleted();
                else if (s.status === 'failed') emitFailed();
            } else if (prevState === 'running' && s.status === 'completed') {
                emitCompleted();
            } else if (prevState === 'running' && s.status === 'failed') {
                emitFailed();
            }

            prevStates[id] = s.status;
        });

        if (newLines.length > 0) {
            setLines(prev => [...prev, ...newLines].slice(-30));
        }
    }, [scenarios, status, completedCount, totalScenarios]);

    const statusLabel = status === 'running' ? `${completedCount}/${totalScenarios}`
        : status?.toUpperCase();

    return (
        <Box sx={{ mx: 3, mt: 1 }}>
            <TerminalConsole
                title="BENCHMARK RUNNER"
                accentColor={PHD_PURPLE}
                statusLabel={statusLabel}
                minHeight={60}
                maxHeight={180}
            >
                {lines.length === 0 && (
                    <ConsoleLine
                        prefix="SYS"
                        text="Waiting for runner output..."
                        color="#555"
                        accentColor={PHD_PURPLE}
                    />
                )}
                {lines.map((line, i) => (
                    <ConsoleLine
                        key={i}
                        prefix={line.prefix}
                        text={line.text}
                        color={line.color}
                        active={i === lines.length - 1}
                        typewriter={i === lines.length - 1}
                        accentColor={PHD_PURPLE}
                    />
                ))}
            </TerminalConsole>
        </Box>
    );
}

// --- Scene content ---
function SceneContent({ edges, nodePositions, scenarioStates, completionPct }) {
    const controlsRef = useRef();

    useFrame(() => {
        if (controlsRef.current) controlsRef.current.update();
    });

    const isAnyRunning = scenarioStates.some(s => s.state === 'running');

    return (
        <>
            <ambientLight intensity={0.04} />
            <directionalLight position={[5, 8, 5]} intensity={0.25} color="#b8a0d0" />
            <pointLight position={[0, 0, 0]} intensity={0.6} color={PHD_PURPLE} distance={10} decay={2} />
            <pointLight position={[3, 2, 3]} intensity={0.2} color={SUCCESS_COLOR} distance={6} decay={2} />
            <pointLight position={[-3, -1, -2]} intensity={0.15} color={PHD_PURPLE_BRIGHT} distance={6} decay={2} />

            <OrbitControls
                ref={controlsRef}
                enableZoom={false}
                enablePan={false}
                autoRotate
                autoRotateSpeed={0.5}
                minPolarAngle={Math.PI * 0.3}
                maxPolarAngle={Math.PI * 0.7}
            />

            <CentralCore completionPct={completionPct} />
            <AmbientParticles />

            {edges.map((edge) => (
                <NetworkEdge
                    key={edge.key}
                    from={edge.fromPos}
                    to={edge.toPos}
                    active={isAnyRunning || completionPct > 0}
                />
            ))}

            {nodePositions.map((pos, i) => (
                <ScenarioNode
                    key={i}
                    position={pos}
                    state={scenarioStates[i]?.state || 'pending'}
                    popPct={scenarioStates[i]?.popPct}
                    label={scenarioStates[i]?.label}
                    index={i}
                />
            ))}
        </>
    );
}

// --- Main exported component ---
export default function BenchmarkVisualization({ run, scenarios }) {
    // total_scenarios from run doc, fallback to progress.total, then scenarios array length
    const totalScenarios = run?.total_scenarios
        || run?.progress?.total
        || scenarios?.length
        || 0;
    const status = run?.status || 'submitted';

    const nodePositions = useMemo(() => {
        const count = Math.max(totalScenarios, 2);
        return generateNodePositions(count);
    }, [totalScenarios]);

    const edges = useMemo(() => {
        const baseEdges = generateEdges(nodePositions);
        // Hub edges from some nodes to center
        const hubEdges = [];
        const step = Math.max(1, Math.floor(nodePositions.length / 4));
        for (let i = 0; i < nodePositions.length; i += step) {
            hubEdges.push({
                from: i,
                to: -1,
                key: `hub-${i}`,
                isHub: true,
            });
        }
        return [...baseEdges, ...hubEdges];
    }, [nodePositions]);

    // Build short label for a scenario config
    const getShortLabel = (scenario) => {
        if (!scenario) return null;
        const cfg = scenario.config || {};
        const parts = [];
        if (cfg.building_type) parts.push(cfg.building_type.replace(/_/g, ' '));
        if (cfg.climate_zone) parts.push(cfg.climate_zone);
        if (parts.length === 0) return scenario.scenario_id?.slice(0, 8) || null;
        return parts.join(' · ');
    };

    // Map scenarios to node states
    const scenarioStates = useMemo(() => {
        const states = [];
        for (let i = 0; i < nodePositions.length; i++) {
            const scenario = scenarios[i];
            const label = getShortLabel(scenario);
            if (!scenario) {
                states.push({ state: 'pending', popPct: null, label: null });
            } else if (scenario.status === 'completed' || scenario.cost_pop_pct != null) {
                states.push({ state: 'completed', popPct: scenario.cost_pop_pct, label });
            } else if (scenario.status === 'failed') {
                states.push({ state: 'failed', popPct: null, label });
            } else if (scenario.status === 'running') {
                states.push({ state: 'running', popPct: null, label });
            } else {
                states.push({ state: 'pending', popPct: null, label });
            }
        }
        return states;
    }, [scenarios, nodePositions.length, totalScenarios, status]);

    const completedCount = scenarioStates.filter(s => s.state === 'completed').length;
    const failedCount = scenarioStates.filter(s => s.state === 'failed').length;
    const runningCount = scenarioStates.filter(s => s.state === 'running').length;
    const completionPct = totalScenarios > 0 ? completedCount / totalScenarios : 0;

    // Resolve hub edges to actual positions (center = [0,0,0])
    const resolvedEdges = useMemo(() => {
        return edges.map(e => {
            if (e.isHub) {
                return { ...e, fromPos: nodePositions[e.from], toPos: [0, 0, 0] };
            }
            return { ...e, fromPos: nodePositions[e.from], toPos: nodePositions[e.to] };
        });
    }, [edges, nodePositions]);

    // Status message
    const statusMessage = useMemo(() => {
        if (status === 'submitted' || status === 'queued') return 'Initializing benchmark...';
        if (status === 'running') {
            if (completedCount === 0 && runningCount === 0) return 'Starting scenario simulations...';
            if (runningCount > 0) return `Running scenario ${completedCount + 1} of ${totalScenarios}`;
            return `Processing ${completedCount} of ${totalScenarios} complete`;
        }
        if (status === 'completed') return 'Benchmark complete — loading results...';
        if (status === 'failed') return 'Benchmark failed';
        return '';
    }, [status, completedCount, runningCount, totalScenarios]);

    return (
        <Box sx={{ width: '100%' }}>
            {/* 3D visualization */}
            <Box sx={{ position: 'relative', width: '100%', height: 500 }}>
                <Canvas
                    camera={{ position: [0, 2.5, 6.5], fov: 45 }}
                    style={{ background: 'transparent' }}
                    gl={{ antialias: true, alpha: true }}
                >
                    <SceneContent
                        nodePositions={nodePositions}
                        edges={resolvedEdges}
                        scenarioStates={scenarioStates}
                        completionPct={completionPct}
                    />
                </Canvas>

                {/* Center status + progress bar overlay */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    pb: 1.5,
                    pointerEvents: 'none',
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: '#aaa',
                        letterSpacing: '0.08em',
                        mb: 0.5,
                    }}>
                        {statusMessage}
                    </Typography>

                    {(status === 'running' || status === 'submitted' || status === 'queued') && (
                        <Box sx={{
                            mx: 'auto',
                            width: '60%',
                            maxWidth: 300,
                            height: 3,
                            bgcolor: 'rgba(155, 89, 182, 0.15)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                width: `${completionPct * 100}%`,
                                height: '100%',
                                bgcolor: PHD_PURPLE,
                                borderRadius: 2,
                                boxShadow: `0 0 8px ${PHD_PURPLE}, 0 0 16px ${PHD_PURPLE}60`,
                                transition: 'width 0.6s ease',
                            }} />
                        </Box>
                    )}

                    {status === 'running' && totalScenarios > 0 && (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: '#666',
                            mt: 0.5,
                        }}>
                            {completedCount} / {totalScenarios} complete
                            {failedCount > 0 ? ` · ${failedCount} failed` : ''}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Terminal log below the 3D scene */}
            <BenchmarkTerminal
                scenarios={scenarios}
                status={status}
                completedCount={completedCount}
                totalScenarios={totalScenarios}
            />
        </Box>
    );
}
